import type {
  IncludedFieldDependentResponse,
  Response,
} from '~/types/api/v2'

import { useFetchWithDefaults } from '~/composables/api/useFetchWithDefaults'

import { type DocumentIdentifier, type DocumentMutationParams, getUrlById as getDocumentByIdUrl } from '../useFetchDocument'

export type IncludableField = 'address'

export const useFetchDocumentParty = () => {
  const getUrl = (documentId: string) => `${getDocumentByIdUrl(documentId)}/parties`

  const { post, put, deleteRequest } = useFetchWithDefaults()

  type DocumentPartyData = App.Data.Document.Metadata.PartyData

  type DynamicResponse<TFieldsToInclude extends string[]> =
    IncludedFieldDependentResponse<
      DocumentPartyData,
      TFieldsToInclude,
      IncludableField
    >

  const create = <TFieldsToInclude extends IncludableField[] = []>({
    payload,
    documentId,
    workspaceId,
    fieldsToInclude,
  }: DocumentMutationParams<App.Data.Payloads.Metadata.Parties.CreatePartyPayload> & {
    fieldsToInclude?: TFieldsToInclude
  }) =>
    post<Response<DynamicResponse<TFieldsToInclude>>>(
      {
        url: getUrl(documentId),
        workspaceId,
        fetchOptions: {
          body: payload,
          params: { include: fieldsToInclude ?? [] },
        },
      },
    )

  const update = <TFieldsToInclude extends IncludableField[] = []>({
    payload,
    partyId,
    documentId,
    workspaceId,
    fieldsToInclude,
  }: DocumentMutationParams<App.Data.Payloads.Metadata.Parties.UpdatePartyPayload> & {
    partyId: string
    fieldsToInclude?: TFieldsToInclude
  }) =>
    put<Response<DynamicResponse<TFieldsToInclude>>>({
      url: `${getUrl(documentId)}/${partyId}`,
      workspaceId,
      fetchOptions: {
        body: payload,
        ...(
          fieldsToInclude?.length
            ? { params: { include: fieldsToInclude ?? [] } }
            : {}
        ),
      },
    })

  const drop
  = ({
    partyId,
    documentId,
    workspaceId,
  }: DocumentIdentifier & { partyId: string }) =>
    deleteRequest({
      url: `${getUrl(documentId)}/${partyId}`,
      workspaceId,
    })

  return {
    create,
    update,
    drop,
  }
}
